import type { LonLat } from "utils/curveLine";
import { boundsOf } from "../shared/mapModel";
import { distanceKm } from "./walk";

/**
 * Where the camera goes on a partner map, as pure functions of sizes in pixels and points on the map. The scene
 * (MapScene) reads the sizes off the page and hands the answer to MapLibre. What the prototype called `fitVisible`
 * (fit the points into the part of the map the open panel leaves free), `fitPts` (fit them into the whole map) and
 * `startFit` (the view the page opens on) are `frameOf` for three kinds of target; a place that is open is a fourth, which goes
 * in as far as its distance from the base allows. A hostel's map opens on the base with the km around it that `openingKm` works
 * out from its highlights, however far the others are: `aroundZoom` gives that view.
 */

export interface Size {
	w: number;
	h: number;
}

/** A rectangle in the map's own pixels. */
export interface Box {
	x: number;
	y: number;
	w: number;
	h: number;
}

export interface Padding {
	top: number;
	right: number;
	bottom: number;
	left: number;
}

/** What to look at. `maxScale` is how far in it may go: 2.2 is 2.2 times the zoom of the map view in the content. */
export type CameraTarget =
	| { kind: "opening" }
	| { kind: "fit"; points: LonLat[]; maxScale: number; /** keep out of the way of the open panel */ panel: boolean }
	/** the base and the place that is open, beside the open panel, as far in as their distance allows (`placeBoost`) */
	| { kind: "place"; base: LonLat; place: LonLat };

export interface CameraContext {
	map: Size;
	/** the open panel in the map's pixels, null when none is open */
	panel: Box | null;
	/** the zoom of the map view in the content */
	zoom: number;
	/** the base and every highlight */
	all: LonLat[];
	/** the base and the highlights within a short walk of it */
	near: LonLat[];
	/** A hostel's map (`map.openAround` in the content): it opens on `center` with `km` around it (`openingKm`) reaching the nearer edge of the map */
	around?: { center: LonLat; km: number };
	/** A phone's opening view keeps the base (`near[0]`) whole on the map, never half under the tabs: the light hostel look's (`keepBaseInView`) */
	keepBase?: boolean;
}

export type Frame = { kind: "bounds"; bounds: [LonLat, LonLat]; padding: Padding; maxZoom: number } | { kind: "look"; center: LonLat; zoom: number };

/** the detail panel floats this far in from the map's left and bottom edge on a wide screen */
const PANEL_INSET = 14;
/** the least height of the map above a panel that covers most of it */
const MIN_FREE_HEIGHT = 120;
/** the least size of the box the points are fitted into, so the margins never eat all of it */
const MIN_FRAME = 40;
/** a map at least this wide opens on the base and all its highlights; a narrower one (a phone) on the base and its close neighbours */
const WIDE_MAP = 500;
/** how far in a narrow map opens, and which highlights it counts as close neighbours (km) */
const OPENING_SCALE = 1.3;
export const OPENING_NEAR_KM = 0.6;

/** px of room around the points: a long name plate beside, the base's marker above, name plates below */
const PANEL_MARGIN = { x: 84, top: 64, bottom: 48 };

/** a phone's layout, as in the page's CSS */
export const PHONE_MAX_WIDTH = 760;
/** a phone's sheet reaches this far up over the map's bottom edge, under its rounded corners */
const SHEET_OVERLAP = 12;

/** The least height of a phone's sheet for it to start at the map's bottom edge, with no strip of the list in between: the rest of the screen under the map, and the overlap. */
export const sheetMinHeight = (viewportHeight: number, mapBottom: number): number => Math.max(0, viewportHeight - mapBottom + SHEET_OVERLAP);

/** Where the open detail panel sits in the map's pixels: a sheet pinned to the bottom of the screen on a phone, a card inset from the map's bottom left corner on a wide screen. */
export function panelBox(map: Size, panel: Size, phone: boolean, mapTop: number, viewportHeight: number): Box {
	return phone
		? { x: 0, y: viewportHeight - panel.h - mapTop, w: map.w, h: panel.h }
		: { x: PANEL_INSET, y: map.h - PANEL_INSET - panel.h, w: panel.w, h: panel.h };
}

/** The part of the map the panel leaves free: the strip above it, or on a wide screen the strip to its right when that is bigger. */
export function freeArea(map: Size, panel: Box | null): Box {
	if (!panel) return { x: 0, y: 0, w: map.w, h: map.h };
	const above: Box = { x: 0, y: 0, w: map.w, h: Math.max(MIN_FREE_HEIGHT, Math.min(map.h, panel.y)) };
	const right: Box = { x: panel.x + panel.w, y: 0, w: Math.max(0, map.w - panel.x - panel.w), h: map.h };
	return above.w * above.h >= right.w * right.h ? above : right;
}

/** Shrinks two margins that sit on one axis of a box so that MIN_FRAME of it is left between them. */
function squeeze(before: number, after: number, size: number): [number, number] {
	const room = Math.max(size - MIN_FRAME, 0);
	const sum = before + after;
	return sum > room ? [(before * room) / sum, (after * room) / sum] : [before, after];
}

/** The padding that makes MapLibre fit points into `free` with `margin` of room inside it, as if `free` were the whole map. */
export function framePadding(map: Size, free: Box, margin: { x: number; top: number; bottom: number }): Padding {
	const [left, right] = squeeze(margin.x, margin.x, free.w);
	const [top, bottom] = squeeze(margin.top, margin.bottom, free.h);
	return {
		top: free.y + top,
		bottom: map.h - (free.y + free.h) + bottom,
		left: free.x + left,
		right: map.w - (free.x + free.w) + right,
	};
}

const centroid = (points: LonLat[]): LonLat => [
	points.reduce((sum, p) => sum + p[0], 0) / points.length,
	points.reduce((sum, p) => sum + p[1], 0) / points.length,
];

/** MapLibre's world is 512 px wide at zoom 0, and doubles with every zoom. */
const WORLD_PX = 512;

/** Where a latitude falls down the Web Mercator world, 0 at the top, 1 at the bottom. */
const mercatorY = (lat: number): number => 0.5 - Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) / (2 * Math.PI);

/** The latitude of a Web Mercator y (see `mercatorY`). */
const latOfMercatorY = (y: number): number => (Math.atan(Math.sinh((0.5 - y) * 2 * Math.PI)) * 180) / Math.PI;

/** px the base's marker and its pulse need on every side to show whole, and a little more under the tabs, where the map's top edge hides the pulse (Ilse's prototype `withBase`) */
const BASE_ROOM = 58;
const BASE_ROOM_TOP = 64;

/**
 * Where to centre a look at `zoom` so that the base's marker is not half under the tabs (the top of a phone's map) or an edge: `center` moved
 * the fewest px that leaves the base `BASE_ROOM` from every side. A look is the centre of some points, and the base can be at the edge of them.
 */
export function keepBaseInView(center: LonLat, zoom: number, base: LonLat, map: Size): LonLat {
	const world = WORLD_PX * 2 ** zoom;
	const x = map.w / 2 + ((base[0] - center[0]) / 360) * world;
	const y = map.h / 2 + (mercatorY(base[1]) - mercatorY(center[1])) * world;
	// how far the base has to move on the screen; the camera moves the other way
	const dx = x < BASE_ROOM ? BASE_ROOM - x : x > map.w - BASE_ROOM ? map.w - BASE_ROOM - x : 0;
	const dy = y < BASE_ROOM_TOP ? BASE_ROOM_TOP - y : y > map.h - BASE_ROOM ? map.h - BASE_ROOM - y : 0;
	return [center[0] - (dx / world) * 360, latOfMercatorY(mercatorY(center[1]) - dy / world)];
}

/** The zoom at which `bounds` just fills a map of `box` px inside `padding`, as MapLibre's `fitBounds` works it out before its own `maxZoom`. Infinity for bounds with no size. */
export function zoomToFit([[west, south], [east, north]]: [LonLat, LonLat], box: Size, padding: Padding): number {
	const room = (px: number) => Math.max(1, px);
	const wide = (east - west) / 360;
	const tall = mercatorY(south) - mercatorY(north);
	return Math.min(
		wide > 0 ? Math.log2(room(box.w - padding.left - padding.right) / (WORLD_PX * wide)) : Infinity,
		tall > 0 ? Math.log2(room(box.h - padding.top - padding.bottom) / (WORLD_PX * tall)) : Infinity
	);
}

/** How many zoom levels over the map's opening zoom a place that is open may be framed at: the nearer it is to the base, the closer the look (Ilse, 19 Oct). */
export function placeBoost(km: number): number {
	if (km < 1.2) return 2.1;
	return km <= 4 ? 1.6 : 1.1;
}

/** A degree of latitude, in km */
const KM_PER_DEGREE = 111.195;
/** MapLibre's top zoom */
const MAX_ZOOM = 22;
const NO_PADDING: Padding = { top: 0, right: 0, bottom: 0, left: 0 };

/** a hostel's map opens with this many of its nearest highlights in view */
export const OPENING_NEAREST = 3;
/** ...within this many km of the hostel at least, so a dense city (Bangkok, Hanoi) is not zoomed in too far, and at most, so an outlier (Doi Inthanon, 57 km) never sets the view */
export const OPENING_MIN_KM = 3;
export const OPENING_MAX_KM = 15;
/** the room left past the farthest of them for its marker and name, so it is not on the edge of the map */
const OPENING_ROOM = 1.15;

/** The km around a hostel that its map opens on, from the distances of its highlights: reaching the 3rd nearest, not under 3 km nor over 15. With fewer than 3 highlights the farthest there is, with none 3 km (Ilse, 19 Oct). */
export function openingKm(distancesKm: number[]): number {
	const nearest = [...distancesKm].sort((a, b) => a - b).slice(0, OPENING_NEAREST);
	const reach = nearest[nearest.length - 1] ?? 0;
	return Math.min(OPENING_MAX_KM, Math.max(OPENING_MIN_KM, reach * OPENING_ROOM));
}

/** The lowest zoom of a map that opens around its base: far enough out to frame a place 100 km away on a phone, beside its open sheet. */
export const AROUND_MIN_ZOOM = 5;

/** The zoom at which `km` out from `center`, in every direction, just reaches the nearer edge of a map of `box` px (the farther edge shows more), within the map's zoom limits. */
export function aroundZoom(center: LonLat, km: number, box: Size): number {
	const dLat = km / KM_PER_DEGREE;
	const dLng = dLat / Math.cos((center[1] * Math.PI) / 180);
	const fit = zoomToFit(
		[
			[center[0] - dLng, center[1] - dLat],
			[center[0] + dLng, center[1] + dLat],
		],
		box,
		NO_PADDING
	);
	return Math.min(MAX_ZOOM, Math.max(AROUND_MIN_ZOOM, fit));
}

/** The zoom a map opens on, which its closer looks are measured from: a hostel's map frames the km around it, any other has its map view's zoom. */
export const homeZoom = (zoom: number, around: CameraContext["around"], box: Size): number => (around ? aroundZoom(around.center, around.km, box) : zoom);

/** The frame for a target: the corners and padding to fit, or the point to look at and how far in. */
export function frameOf(target: CameraTarget, ctx: CameraContext): Frame {
	const { map, zoom, around } = ctx;
	// A hostel's map opens on what is a walk or a short ride from the hostel, not on every highlight, however far
	if (target.kind === "opening" && around) return { kind: "look", center: around.center, zoom: aroundZoom(around.center, around.km, map) };
	if (target.kind === "opening" && map.w < WIDE_MAP) {
		const look = zoom + Math.log2(OPENING_SCALE);
		// `near` starts with the base
		const middle = centroid(ctx.near);
		return { kind: "look", center: ctx.keepBase ? keepBaseInView(middle, look, ctx.near[0], map) : middle, zoom: look };
	}

	const withPanel = target.kind === "place" || (target.kind === "fit" && target.panel);
	const free = freeArea(map, withPanel ? ctx.panel : null);
	const margin = withPanel ? PANEL_MARGIN : { x: Math.min(80, map.w * 0.15), top: Math.min(100, map.h * 0.18), bottom: Math.min(100, map.h * 0.18) };
	const fit = (points: LonLat[], maxZoom: number): Frame => ({ kind: "bounds", bounds: boundsOf(points)!, padding: framePadding(map, free, margin), maxZoom });
	if (target.kind === "place") {
		return fit([target.base, target.place], homeZoom(zoom, around, map) + placeBoost(distanceKm(target.base, target.place)));
	}
	return fit(target.kind === "opening" ? ctx.all : target.points, zoom + Math.log2(target.kind === "opening" ? 2.2 : target.maxScale));
}
